import { cn } from '@eo/shared';

import {
  BUDGET_BUCKETS,
  COMPOSITION_CLASS,
  COMPOSITION_KEYS,
  COMPOSITION_LABELS,
} from './constants';
import { barPercent, compositionSegments, formatHours } from './helpers';
import type { HoursCompositionProps } from './types';

// Each bucket's width is its share of the engagement, so fixed and variable
// compare at a glance; inside, the split is who holds the hours.
export function HoursComposition({ totals }: HoursCompositionProps) {
  const engagement = totals.budget.fixed + totals.budget.variable;

  if (engagement === 0) {
    return (
      <p className="text-sm text-text-tertiary">No budget set for this portfolio’s clients.</p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <ul className="flex flex-wrap gap-4 text-xs text-text-tertiary">
        {COMPOSITION_KEYS.map((key) => (
          <li key={key} className="flex items-center gap-2">
            <span className={cn('size-2.5 rounded-[2px]', COMPOSITION_CLASS[key])} />
            {COMPOSITION_LABELS[key]}
          </li>
        ))}
      </ul>

      <div className="flex flex-col gap-4 sm:flex-row">
        {BUDGET_BUCKETS.map(({ key, label }) => {
          const budget = totals.budget[key];
          const segments = compositionSegments(totals, key);
          const scale = Math.max(
            budget,
            segments.reduce((s, seg) => s + seg.hours, 0),
          );
          const reading = `${label}, ${formatHours(budget)}: ${segments
            .map((seg) => `${formatHours(seg.hours)} ${seg.label}`)
            .join(', ')}`;
          return (
            <div
              key={key}
              className="flex min-w-0 flex-col gap-2 sm:basis-0"
              // Side by side, each bucket grows by its share of the engagement,
              // never below a readable quarter; stacked on a phone, both span.
              style={{ flexGrow: Math.max(25, (budget / engagement) * 100) }}
            >
              <div className="flex items-baseline justify-between gap-2 text-sm">
                <span className="text-text-primary">{label}</span>
                <span className="tabular-nums text-text-secondary">{formatHours(budget)}</span>
              </div>
              <div
                role="img"
                aria-label={reading}
                title={reading}
                className="flex h-6 gap-[2px] overflow-hidden rounded-[4px]"
              >
                {segments
                  .filter((seg) => seg.hours > 0)
                  .map((seg) => (
                    <div
                      key={seg.key}
                      className={COMPOSITION_CLASS[seg.key]}
                      style={{ width: barPercent(seg.hours, scale) }}
                    />
                  ))}
              </div>
              <ul className="flex flex-col gap-0.5 text-xs tabular-nums text-text-tertiary">
                {segments.map((seg) => (
                  <li key={seg.key}>
                    {formatHours(seg.hours)} {seg.label}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}
